import { List, useTable } from "@refinedev/antd";
import { Table, Space, Button, Avatar, Typography } from "antd";
import { GlassCard } from "../../shared/components";
import { EditOutlined, EyeOutlined, LinkOutlined } from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";
import { useGo } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { useAvatarPalette } from "../../shared/styles";
import { getAvatarColor, getInitialsFromName } from "../../shared/utils";
import { useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { ServiceProviderCreateModal } from "./ServiceProviderCreateModal";
import { ServiceProviderEditModal } from "./ServiceProviderEditModal";
import { ServiceProviderProductMappingModal } from "./ServiceProviderProductMappingModal";

const { Text } = Typography;

export const ServiceProviderList = ({ hideHeader }: { hideHeader?: boolean } = {}) => {
  const { tableProps, tableQueryResult } = useTable({
    resource: "service-providers",
    syncWithLocation: true,
  });

  const _t = useTranslate();
  const go = useGo();
  const avatarPalette = useAvatarPalette();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingServiceProviderId, setEditingServiceProviderId] = useState<
    number | string | null
  >(null);
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [mappingServiceProviderId, setMappingServiceProviderId] = useState<
    number | string | null
  >(null);

  return (
    <div className="max-w-7xl mx-auto">
      <List
        title={hideHeader ? false : undefined}
        breadcrumb={hideHeader ? false : undefined}
        headerButtons={({ createButtonProps: _ }) => [
          <Button
            key="create"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            {_t("service-providers.titles.create")}
          </Button>,
        ]}
      >
        <style>{`
                .service-providers-list-table-view .ant-table { background: transparent !important; }
                .service-providers-list-table-view .ant-table-thead > tr > th { background: transparent !important; }
                .service-providers-list-table-view .ant-table-tbody > tr > td { background: transparent !important; }
            `}</style>
        <GlassCard className="service-providers-list-table-view !p-0 overflow-hidden">
          <Table {...tableProps} rowKey="id">
            <Table.Column
              title={_t("service-providers.fields.name")}
              key="serviceProvider"
              width={250}
              render={(_, record: any) => (
                <Space>
                  <Avatar
                    shape="square"
                    size={44}
                    style={{
                      backgroundColor: getAvatarColor(record.id, avatarPalette),
                      fontWeight: 600,
                    }}
                  >
                    {getInitialsFromName(record.name)}
                  </Avatar>
                  <Text strong>{record.name}</Text>
                </Space>
              )}
            />
            <Table.Column
              dataIndex="createdAt"
              title={_t("service-providers.fields.createdAt")}
              render={(value) => (
                <DateField value={value} format="DD/MM/YYYY HH:mm" />
              )}
            />
            <Table.Column
              title={_t("table.actions")}
              dataIndex="actions"
              render={(_, record) => (
                <Space>
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => {
                      go({
                        to: `/service-providers/show/${record.id}`,
                      });
                    }}
                  />
                  <Button
                    icon={<LinkOutlined />}
                    title={_t("service-providers.actions.productMapping")}
                    onClick={() => {
                      setMappingServiceProviderId(record.id);
                      setMappingModalOpen(true);
                    }}
                  />
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingServiceProviderId(record.id);
                      setEditModalOpen(true);
                    }}
                  />
                </Space>
              )}
            />
          </Table>
        </GlassCard>
        <ServiceProviderCreateModal
          open={createModalOpen}
          onCancel={() => setCreateModalOpen(false)}
          onSuccess={() => tableQueryResult?.refetch()}
        />
        <ServiceProviderEditModal
          open={editModalOpen}
          serviceProviderId={editingServiceProviderId}
          onCancel={() => {
            setEditModalOpen(false);
            setEditingServiceProviderId(null);
          }}
          onSuccess={() => tableQueryResult?.refetch()}
        />
        <ServiceProviderProductMappingModal
          open={mappingModalOpen}
          serviceProviderId={mappingServiceProviderId}
          onCancel={() => {
            setMappingModalOpen(false);
            setMappingServiceProviderId(null);
          }}
          onSuccess={() => tableQueryResult?.refetch()}
        />
      </List>
    </div>
  );
};
